import { Check, Copy, Download, Share2 } from "lucide-react";
import { useMemo, useState } from "react";

import logoWhiteUrl from "@/assets/svg/logo-white.svg";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";
import { canShareImages } from "@/shared/utils/shareImage";

import { usePayinQrShare } from "../hooks/usePayinQrShare";

export interface PayinQrCardProps {
  /** PNG data URL from `pixQrDataUrl`; null while it renders. */
  src: string | null;
  rail: string;
  /** Under the code: the expiry, the countdown or the accepted range, which each surface words its own way. */
  footer?: string;
  /** Printed on the shared image only: on screen the amount already sits next to the card. */
  amount?: string;
  /** Replaces `footer` on the shared image (null prints none): a countdown would be stale in a chat. */
  shareExpiry?: string | null;
  /** Only for a QR with no fixed amount: a transfer typed to the key settles the same way scanning does. */
  brebKey?: string | null;
  className?: string;
}

// No logo over the modules: it would force a denser code, so the brand lives in the frame.
export function PayinQrCard({
  src,
  rail,
  footer,
  amount,
  shareExpiry,
  brebKey,
  className,
}: PayinQrCardProps) {
  const { t } = useTranslations("payins");
  const railLabel = formatRail(rail);
  const title = t("qr_card_title", { rail: railLabel });
  const hint = t("qr_card_hint");
  const amountLabel = t("qr_card_amount_label");
  const keyLabel = t("qr_card_key_label");
  const expiry = shareExpiry === undefined ? footer : (shareExpiry ?? undefined);
  const [canShare] = useState(() => canShareImages());
  const { copiedValue, copy } = useCopyToClipboard(t("breb_key_copy_error"));

  const imageModel = useMemo(
    () =>
      src
        ? {
            qrSrc: src,
            railLabel,
            title,
            hint,
            amountLabel,
            amount,
            expiry,
            keyLabel: brebKey ? keyLabel : undefined,
            keyValue: brebKey ?? undefined,
          }
        : null,
    [src, railLabel, title, hint, amountLabel, amount, expiry, brebKey, keyLabel],
  );
  const fileName = brebKey
    ? `coloca-qr-${rail.toLowerCase()}-${brebKey.replace(/^@/, "")}.png`
    : `coloca-qr-${rail.toLowerCase()}.png`;
  const { ready, share, download } = usePayinQrShare(imageModel, fileName);

  return (
    <figure
      className={cn(
        "mx-auto w-full max-w-72 rounded-3xl bg-[#00735C] p-3 text-white shadow-sm sm:max-w-96",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 px-2 pt-1 pb-3">
        <img src={logoWhiteUrl} alt="Coloca" className="h-4" />
        <span className="rounded-full bg-black/15 px-2.5 py-0.5 text-xs font-semibold tracking-wide">
          {railLabel}
        </span>
      </div>
      <div className="rounded-xl bg-white p-1">
        {src ? (
          // Smoothing a dense code down to screen size greys its module edges, and a camera reads that as noise.
          <img
            src={src}
            alt={`QR ${railLabel}`}
            className="block aspect-square w-full [image-rendering:pixelated]"
          />
        ) : (
          <div aria-hidden="true" className="aspect-square w-full animate-pulse rounded-lg bg-neutral-100" />
        )}
      </div>
      <figcaption className="px-2 pt-3 pb-1 text-center">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-0.5 text-xs text-white/90">{hint}</p>
        {footer && <p className="mt-2 text-xs font-medium text-white">{footer}</p>}
        {brebKey && (
          <div className="mt-3 rounded-xl bg-black/15 px-3 py-2 text-left">
            <p className="text-xs text-white/90">{keyLabel}</p>
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate font-mono text-base font-semibold tracking-tight">
                {brebKey}
              </span>
              <ColocaIconButton
                size="sm"
                variant="ghost"
                icon={copiedValue === brebKey ? Check : Copy}
                onClick={() => void copy(brebKey, t("breb_key_copied"))}
                aria-label={t("breb_key_copy")}
                className="shrink-0 text-white hover:bg-white/15 hover:text-white focus-visible:outline-white"
              />
            </div>
          </div>
        )}
        {src && (
          <div className={cn("mt-3 grid gap-2", canShare && "grid-cols-2")}>
            {canShare && (
              <ColocaButton
                size="sm"
                icon={Share2}
                disabled={!ready}
                onClick={() => void share()}
                className="text-foreground"
              >
                {t("qr_card_share")}
              </ColocaButton>
            )}
            <ColocaButton
              size="sm"
              icon={Download}
              disabled={!ready}
              onClick={download}
              className="text-foreground"
            >
              {t("qr_card_download")}
            </ColocaButton>
          </div>
        )}
      </figcaption>
    </figure>
  );
}
